/**
 * ReasoningPlanView — renders the live multi-step reasoning plan returned by the
 * AI Copilot for orchestrated workflows (e.g., the Phase 2 north-star
 * progression-since-treatment workflow).
 *
 * Displays each step with its name, action type, success state, and latency.
 * The plan is collapsible and shows an overall status chip.
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Collapse,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { ReasoningStep } from '../services/aiCopilotService';
import { typography } from '~/theme';

interface ReasoningPlanViewProps {
  steps: ReasoningStep[];
}

const ACTION_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'> = {
  agent: 'primary',
  tool: 'info',
  rag: 'success',
};

const ReasoningPlanView: React.FC<ReasoningPlanViewProps> = ({ steps }) => {
  const [expanded, setExpanded] = useState(true);

  if (steps.length === 0) return null;

  const totalLatency = steps.reduce((sum, s) => sum + s.latency_ms, 0);
  const allSuccess = steps.every(s => s.success);
  const failedSteps = steps.filter(s => !s.success);

  const formatStepName = (name: string) =>
    name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());

  return (
    <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          bgcolor: allSuccess ? 'success.50' : 'warning.50',
          '&:hover': { bgcolor: allSuccess ? 'success.100' : 'warning.100' },
        }}
      >
        {allSuccess ? (
          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
        ) : (
          <ErrorIcon sx={{ fontSize: 16, color: 'warning.main' }} />
        )}
        <Typography variant="caption" fontWeight={typography.label.fontWeight}>
          Reasoning Plan — {steps.length} steps
        </Typography>
        {!allSuccess && (
          <Chip
            label={`${failedSteps.length} failed`}
            size="small"
            color="error"
            sx={{ height: 16, fontSize: '0.6rem' }}
          />
        )}
        <Chip
          label={`${totalLatency}ms`}
          size="small"
          sx={{ height: 16, fontSize: '0.6rem', ml: 'auto' }}
        />
        {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, py: 1 }}>
          {steps.map((step, i) => (
            <Tooltip
              key={i}
              title={step.error || ''}
              disableHoverListener={!step.error}
              arrow
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  borderLeft: '2px solid',
                  borderLeftColor: step.success ? 'success.main' : 'error.main',
                  pl: 1.5,
                  mb: 0.5,
                  opacity: step.success ? 1 : 0.85,
                }}
              >
                {step.success ? (
                  <CheckCircleIcon sx={{ fontSize: 13, color: 'success.main' }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 13, color: 'error.main' }} />
                )}
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {formatStepName(step.name)}
                </Typography>
                <Chip
                  label={step.action}
                  size="small"
                  variant="outlined"
                  color={ACTION_COLORS[step.action] || 'default'}
                  sx={{ height: 14, fontSize: '0.58rem' }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.62rem', minWidth: 40, textAlign: 'right' }}
                >
                  {step.latency_ms}ms
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ReasoningPlanView;
